import Payment from "./Payment";
import { IPaymentProcessor, PaymentProcesserEnum } from "./PaymentProcessor";
import { configService } from "../Config/ConfigService";
import { CardXEnvironmentConfiguration } from "../Config/EnvironmentConfiguration";
import isNullOrUndefined from "../../Components/Shared/Utility/isNullOrUndefined";

interface ICardXInstanceOptions {
    amount : string;
    cardElement: Element
    mode ?: string;
    includeBillingFields : boolean;
    isBillingRequired : boolean;
    callbackFn : Function;
    accountNumber : string;
}

interface ICardXScriptOptions {
    scriptId : string;
    srcPath : string;
    account: string;
    dataButton : string;
    callbackFnName : string;
    mode : string;
    amount : string | number;
    billingInclude : boolean;
    billingRequired: boolean;
    accountNumber : string;
}

class CardXClient implements IPaymentProcessor {

    _window : any;
    _scriptElement : HTMLScriptElement = null;
    _callback : Function;
    defaultTestAmount = (1.0).toString();

    constructor() {
        this._window = window;
    }

    getPaymentProcessorId(): PaymentProcesserEnum {
        return PaymentProcesserEnum.CardX;
    }

    async tokenize(payment: Payment): Promise<Payment> {
        return payment;
    }

    private async getCardXEnvironment() : Promise<CardXEnvironmentConfiguration> {
        var config = await configService.getConfiguration();
        var cardXEnvironment = (config.CardX.Environment === "Production") ? config.CardX.Production : config.CardX.Test;
        return cardXEnvironment;
    }

    private isTestScript(script : string) : boolean {
        return script.includes("test");
    }

    public async initializeCardX(instanceOpts : ICardXInstanceOptions) {
        const cardXEnvironment = await this.getCardXEnvironment();
        
        const scriptOpts : ICardXScriptOptions = {
            scriptId : 'cardx_lightbox_script',
            dataButton: `#${instanceOpts.cardElement.id}`,
            srcPath: cardXEnvironment.Script,
            account : cardXEnvironment.Account,
            callbackFnName: '_cardXCallback',
            mode:  instanceOpts.mode,
            amount: instanceOpts.amount,
            billingInclude: instanceOpts.includeBillingFields,
            billingRequired: instanceOpts.isBillingRequired,
            accountNumber: instanceOpts.accountNumber
        }

        this._window._cardXCallback = this.cardXCallback.bind(this);
        this._callback = instanceOpts.callbackFn;

        this.injectScript(scriptOpts);
    }

    private cardXCallback(err : any, response : any) {
        this._callback(err, response);
    }

    private getAmountForEnvironment(scriptPath : string, amount : string| number) : string {
        return this.isTestScript(scriptPath) ? this.defaultTestAmount : amount.toString() ;
    }

    private injectScript(opts : ICardXScriptOptions) {
        this._scriptElement = document.createElement('script');
        this._scriptElement.id = opts.scriptId;
        this._scriptElement.src = opts.srcPath;
        this._scriptElement.setAttribute('data-account', opts.account);
        this._scriptElement.setAttribute('data-lightboxamount', opts.amount.toString());
        this._scriptElement.setAttribute('data-amount', this.getAmountForEnvironment(opts.srcPath, opts.amount));
        this._scriptElement.setAttribute('data-button', opts.dataButton);
        this._scriptElement.setAttribute('data-callback', opts.callbackFnName);
        this._scriptElement.setAttribute('data-mode', 'payment');
        this._scriptElement.setAttribute('data-billingInclude', opts.billingInclude === true ? 'true' : 'false');
        this._scriptElement.setAttribute('data-billingRequired', opts.billingRequired === true ? 'true' : 'false');
        if (!isNullOrUndefined(opts.accountNumber)) {
            this._scriptElement.setAttribute('data-accountIdentifier', opts.accountNumber);
            this._scriptElement.setAttribute('data-accountIdentifierEditable', 'false');
        }
        document.body.appendChild(this._scriptElement);
    }

    updateScriptAttributes(opts : Partial<ICardXScriptOptions>) {
        if (this._scriptElement === null) return;

        if (!isNullOrUndefined(opts.amount)){
            this._scriptElement.setAttribute('data-lightboxamount', opts.amount.toString());
            this._scriptElement.setAttribute('data-amount', this.getAmountForEnvironment(this._scriptElement.src, opts.amount));
        }
        
        if (!isNullOrUndefined(opts.accountNumber)){
            this._scriptElement.setAttribute('data-accountIdentifier', opts.accountNumber);
            this._scriptElement.setAttribute('data-accountIdentifierEditable', 'false');
        }

    }

    public disposeCardX() {
        if (this._window._cardXCallback){
            delete this._window._cardXCallback;
        }

        if (this._scriptElement != null){
            document.body.removeChild(this._scriptElement);
            this._scriptElement = null;
        }
    }
}


export default new CardXClient();